<template>
  <c-modal
    v-model:open="isModalOpen"
    :class="b()"
    :content-class="b(formModifiers)"
    size="500"
  >
    <div
      v-if="eifuStore.runningRequests.apiRequestHardCopy"
      :class="b('progress')"
    >
      <e-progress />
    </div>
    <template v-else-if="showConfirmation">
      <h2 :class="b('title')">
        {{ matchThemeTranslation(themeConfig.translations.titleConfirmation) }}
      </h2>
      <p>
        {{ matchThemeTranslation(themeConfig.translations.textConfirmationMessage) }}
      </p>
      <div :class="b('close-button-wrapper')">
        <e-button
          :class="b('close-button')"
          :width="$viewport.isSm ? 'auto' : 'full'"
          variant="primary"
          spacing="600"
          @click="isModalOpen = false"
        >
          {{ $t('c-electronic-instruction-request.buttonLabelClose') }}
        </e-button>
      </div>
    </template>
    <form
      v-else
      :class="b('form', formModifiers)"
      novalidate
      @submit.prevent="onSubmit"
    >
      <h2 :class="b('title')">
        {{ matchThemeTranslation(themeConfig.translations.formTitle) }}
      </h2>
      <div :class="b('download')">
        <p :class="b('download-title')">
          {{ matchThemeTranslation(themeConfig.translations.downloadTitle) }}
        </p>
        <p :class="b('download-disclaimer')">
          {{ matchThemeTranslation(themeConfig.translations.downloadDisclaimer) }}
        </p>
        <e-button
          :class="b('download-button')"
          width="auto"
          variant="primary"
          spacing="600"
          @click="onClickDownload"
        >
          {{ matchThemeTranslation(themeConfig.translations.downloadButton) }}
        </e-button>
      </div>
      <div :class="b('meta')">
        <div :class="b('brand')">
          {{ getElectronicInstructionBrandNames(item) }}
        </div>
        <dl :class="b('properties')">
          <template v-if="item.referenceNumbers && item.referenceNumbers.length">
            <div
              :ref="PROPERTY_REF_MAP.REFERENCE_NUMBER"
              :class="b('property', getModifiersForLimitedProperty(PROPERTY_REF_MAP.REFERENCE_NUMBER))"
            >
              <dt :class="b('property-name')">
                {{ $t('c-electronic-instruction-request.referenceNumber') }}
              </dt>
              <dd :class="b('property-value')">
                {{ item.referenceNumbers.join(', ') }}
              </dd>
            </div>
            <div :class="b('property-toggle-wrapper')">
              <button
                :class="b('property-toggle')"
                type="button"
                @click="onPropertyCollapseToggle(PROPERTY_REF_MAP.REFERENCE_NUMBER)"
              >
                <template v-if="expandedProperties[PROPERTY_REF_MAP.REFERENCE_NUMBER]">
                  {{ $t('c-electronic-instruction-request.propertyToggleLabelCollapse') }}
                </template>
                <template v-else>
                  {{ $t('c-electronic-instruction-request.propertyToggleLabelExpand') }}
                </template>
              </button>
            </div>
          </template>
          <template v-if="item.udiDiNumbers && item.udiDiNumbers.length">
            <div
              :ref="PROPERTY_REF_MAP.UDI_DI_NUMBER"
              :class="b('property', getModifiersForLimitedProperty(PROPERTY_REF_MAP.UDI_DI_NUMBER))"
            >
              <dt :class="b('property-name')">
                {{ $t('c-electronic-instruction-request.udiDiNumber') }}
              </dt>
              <dd :class="b('property-value')">
                {{ item.udiDiNumbers.join(', ') }}
              </dd>
            </div>
            <div :class="b('property-toggle-wrapper')">
              <button
                :class="b('property-toggle')"
                type="button"
                @click="onPropertyCollapseToggle(PROPERTY_REF_MAP.UDI_DI_NUMBER)"
              >
                <template v-if="expandedProperties[PROPERTY_REF_MAP.UDI_DI_NUMBER]">
                  {{ $t('c-electronic-instruction-request.propertyToggleLabelCollapse') }}
                </template>
                <template v-else>
                  {{ $t('c-electronic-instruction-request.propertyToggleLabelExpand') }}
                </template>
              </button>
            </div>
          </template>
        </dl>
      </div>
      <div :class="b('form-row')">
        <component
          v-model="formData.gender"
          :is="matchTheme(themeConfig.controls.cSelect)"
          control="c-select"
          :options="genderOptions"
          :label="`${matchThemeTranslation(themeConfig.translations.inputLabelGender)}*`"
          :placeholder="matchThemeTranslation(themeConfig.translations.inputPlaceholderGender)"
          :state="v$.formData.gender.$error ? 'error' : 'default'"
          name="gender"
        >
          <template v-if="v$.formData.gender.$error">
            {{ $t('c-electronic-instruction-request.errorNotificationFieldRequired') }}
          </template>
        </component>
      </div>
      <div :class="b('form-row')">
        <component
          v-model="formData.title"
          :is="matchTheme(themeConfig.controls.eInput)"
          control="e-input"
          :label="matchThemeTranslation(themeConfig.translations.inputLabelTitle)"
          :placeholder="matchThemeTranslation(themeConfig.translations.inputPlaceholderTitle)"
          name="title"
        />
      </div>
      <div :class="b('form-row')">
        <component
          v-model="formData.firstName"
          :is="matchTheme(themeConfig.controls.eInput)"
          control="e-input"
          :label="`${matchThemeTranslation(themeConfig.translations.inputLabelFirstName)}*`"
          :placeholder="matchThemeTranslation(themeConfig.translations.inputPlaceholderFirstName)"
          :state="v$.formData.firstName.$error ? 'error' : 'default'"
          autocomplete="given-name"
          name="firstName"
        >
          <template v-if="v$.formData.firstName.$error">
            {{ $t('c-electronic-instruction-request.errorNotificationFieldRequired') }}
          </template>
        </component>
      </div>
      <div :class="b('form-row')">
        <component
          v-model="formData.lastName"
          :is="matchTheme(themeConfig.controls.eInput)"
          control="e-input"
          :label="`${matchThemeTranslation(themeConfig.translations.inputLabelLastName)}*`"
          :placeholder="matchThemeTranslation(themeConfig.translations.inputPlaceholderLastName)"
          :state="v$.formData.lastName.$error ? 'error' : 'default'"
          autocomplete="family-name"
          name="lastName"
        >
          <template v-if="v$.formData.lastName.$error">
            {{ $t('c-electronic-instruction-request.errorNotificationFieldRequired') }}
          </template>
        </component>
      </div>
      <div :class="b('form-row')">
        <component
          v-model="formData.companyName"
          :is="matchTheme(themeConfig.controls.eInput)"
          control="e-input"
          :label="`${matchThemeTranslation(themeConfig.translations.inputLabelCompanyName)}*`"
          :placeholder="matchThemeTranslation(themeConfig.translations.inputPlaceholderCompanyName)"
          :state="v$.formData.companyName.$error ? 'error' : 'default'"
          autocomplete="organization"
          name="companyName"
        >
          <template v-if="v$.formData.companyName.$error">
            {{ $t('c-electronic-instruction-request.errorNotificationFieldRequired') }}
          </template>
        </component>
      </div>
      <div :class="b('form-row')">
        <component
          v-model="formData.address"
          :is="matchTheme(themeConfig.controls.eInput)"
          control="e-input"
          :label="`${matchThemeTranslation(themeConfig.translations.inputLabelAddress)}*`"
          :placeholder="matchThemeTranslation(themeConfig.translations.inputPlaceholderAddress)"
          :state="v$.formData.address.$error ? 'error' : 'default'"
          autocomplete="street-address"
          name="address"
        >
          <template v-if="v$.formData.address.$error">
            {{ $t('c-electronic-instruction-request.errorNotificationFieldRequired') }}
          </template>
        </component>
      </div>
      <div :class="b('form-row')">
        <component
          v-model="formData.zipCode"
          :is="matchTheme(themeConfig.controls.eInput)"
          control="e-input"
          :label="`${matchThemeTranslation(themeConfig.translations.inputLabelZipCode)}*`"
          :placeholder="matchThemeTranslation(themeConfig.translations.inputPlaceholderZipCode)"
          :state="v$.formData.zipCode.$error ? 'error' : 'default'"
          autocomplete="postal-code"
          name="zipCode"
        >
          <template v-if="v$.formData.zipCode.$error">
            {{ $t('c-electronic-instruction-request.errorNotificationFieldRequired') }}
          </template>
        </component>
      </div>
      <div :class="b('form-row')">
        <component
          v-model="formData.city"
          :is="matchTheme(themeConfig.controls.eInput)"
          control="e-input"
          :label="`${matchThemeTranslation(themeConfig.translations.inputLabelCity)}*`"
          :placeholder="matchThemeTranslation(themeConfig.translations.inputPlaceholderCity)"
          :state="v$.formData.city.$error ? 'error' : 'default'"
          autocomplete="on"
          name="city"
        >
          <template v-if="v$.formData.city.$error">
            {{ $t('c-electronic-instruction-request.errorNotificationFieldRequired') }}
          </template>
        </component>
      </div>
      <div :class="b('form-row')">
        <component
          v-model="formData.country"
          :is="matchTheme(themeConfig.controls.eInput)"
          control="e-input"
          :label="`${matchThemeTranslation(themeConfig.translations.inputLabelCountry)}*`"
          :placeholder="matchThemeTranslation(themeConfig.translations.inputPlaceholderCountry)"
          :state="v$.formData.country.$error ? 'error' : 'default'"
          autocomplete="country-name"
          name="country"
        >
          <template v-if="v$.formData.country.$error">
            {{ $t('c-electronic-instruction-request.errorNotificationFieldRequired') }}
          </template>
        </component>
      </div>
      <div :class="b('form-row')">
        <component
          v-model="formData.email"
          :is="matchTheme(themeConfig.controls.eInput)"
          control="e-input"
          :label="`${matchThemeTranslation(themeConfig.translations.inputLabelEmail)}*`"
          :placeholder="matchThemeTranslation(themeConfig.translations.inputPlaceholderEmail)"
          :state="v$.formData.email.$error ? 'error' : 'default'"
          type="email"
          autocomplete="email"
          name="email"
        >
          <template v-if="v$.formData.email.$error">
            {{ $t('c-electronic-instruction-request.errorNotificationEmail') }}
          </template>
        </component>
      </div>
      <div :class="b('form-row')">
        <component
          v-model="formData.phone"
          :is="matchTheme(themeConfig.controls.eInput)"
          control="e-input"
          :label="matchThemeTranslation(themeConfig.translations.inputLabelPhone)"
          :placeholder="matchThemeTranslation(themeConfig.translations.inputPlaceholderPhone)"
          inputmode="tel"
          name="phone"
        />
      </div>
      <div :class="b('form-row')">
        <component
          v-model="formData.comment"
          :is="matchTheme(themeConfig.controls.eTextarea)"
          control="e-textarea"
          :label="matchThemeTranslation(themeConfig.translations.inputLabelComment)"
          :placeholder="matchThemeTranslation(themeConfig.translations.inputPlaceholderComment)"
          name="comment"
        />
      </div>
      <div :class="b('form-row')">
        <e-checkbox
          v-model="formData.acceptGeneralTermsOfUse"
          :state="v$.formData.acceptGeneralTermsOfUse.$error ? 'error' : 'default'"
          name="acceptGeneralTermsOfUse"
        >
          <i18n-t
            keypath="c-electronic-instruction-request.inputLabelGeneralTermsOfUse"
            scope="global"
          >
            <template #link>
              <a
                :href="sessionStore.urls.generalTermsOfUsePage"
                target="_blank"
                rel="noopener noreferrer"
                :class="b('terms-link')"
              >
                {{ $t('c-electronic-instruction-request.inputLabelGeneralTermsOfUseLink') }}*
              </a>
            </template>
          </i18n-t>
        </e-checkbox>
      </div>
      <div :class="b('footer')">
        <e-button
          :class="b('button-submit')"
          :width="$viewport.isSm ? 'auto' : 'full'"
          variant="primary"
          type="submit"
          spacing="600"
        >
          {{ $t('c-electronic-instruction-request.buttonLabelSubmitForm') }}
        </e-button>
        <button
          v-if="isTheme('default')"
          :class="b('button-back')"
          type="button"
          @click="isModalOpen = false"
        >
          <e-icon-sprite
            icon="i-chevron-1--left"
            size="22"
          />
          {{ $t('c-electronic-instruction-request.buttonLabelBack') }}
        </button>
      </div>
    </form>
  </c-modal>
</template>

<script>
  import { useVuelidate } from '@vuelidate/core';
  import { email, required } from '@vuelidate/validators';
  import useEifuStore from '@/stores/eifu';
  import useSessionStore from '@/stores/session';
  import cModal from '@/components/c-modal.vue';
  import eButton from '@/elements/e-button.vue';
  import eIconSprite from '@/elements/e-icon-sprite.vue';
  import eProgress from '@/elements/e-progress.vue';
  import downloadFileByUrl from '@/helpers/download-file.js';
  import getElectronicInstructionBrandNames from '@/helpers/get-electronic-instruction-brand-names.js';
  import themes from '@/mixins/themes.js';

  /**
   * A list of properties with limited height applied (accordion).
   *
   * @type {Object}
   */
  const PROPERTY_REF_MAP = {
    REFERENCE_NUMBER: 'property--reference-number',
    UDI_DI_NUMBER: 'property--udi-di-number',
  };

  /**
   * Renders a modal with a form to request a hard copy of an electronic instruction.
   */
  export default {
    name: 'c-electronic-instruction-request',

    components: {
      eIconSprite,
      eButton,
      eProgress,
      cModal,
    },
    mixins: [themes],

    props: {
      /**
       * Expects an electronic instruction item.
       */
      item: {
        type: Object,
        required: false,
        default: () => null,
      },
    },

    setup() {
      return {
        v$: useVuelidate(),
        eifuStore: useEifuStore(),
        sessionStore: useSessionStore(),
      };
    },

    data() {
      return {
        PROPERTY_REF_MAP,

        /**
         * @type {Object} Holds the form data.
         */
        formData: {
          gender: null,
          title: '',
          firstName: '',
          lastName: '',
          companyName: '',
          address: '',
          zipCode: '',
          city: '',
          country: '',
          email: '',
          phone: '',
          comment: '',
          acceptGeneralTermsOfUse: false,
        },

        /**
         * @type {Boolean} Flag for showing the confirmation a request was submitted.
         */
        showConfirmation: false,

        /**
         * @type {Array.<Object>} Holds the options for the gender select.
         */
        genderOptions: [
          {
            label: this.$t('c-electronic-instruction-request.genderOptionFemale'),
            value: 'female',
          },
          {
            label: this.$t('c-electronic-instruction-request.genderOptionMale'),
            value: 'male',
          },
          {
            label: this.$t('c-electronic-instruction-request.genderOptionNotDisclosed'),
            value: 'not-disclosed',
          },
        ],

        /**
         * @type {Array.<String>} Holds a list of ref identifiers, that relate to properties with text overflow.
         */
        overflowingProperties: [],

        /**
         * @type {Object} Holds flags for the open/close state of properties with limited height.
         */
        expandedProperties: {},

        /**
         * @type {Object} Holds theme specific options for the component.
         */
        themeConfig: {
          controls: {
            eInput: {
              default: 'e-input',
              sagemax: 'c-labeled-control',
            },
            eTextarea: {
              default: 'e-textarea',
              sagemax: 'c-labeled-control',
            },
            cSelect: {
              default: 'c-select',
              sagemax: 'c-labeled-control',
            },
          },
          translations: {
            titleConfirmation: {
              default: 'c-electronic-instruction-request.titleConfirmation',
              sagemax: 'c-sagemax-electronic-instruction-request.titleConfirmation',
            },
            textConfirmationMessage: {
              default: 'c-electronic-instruction-request.textConfirmationMessage',
              sagemax: 'c-sagemax-electronic-instruction-request.textConfirmationMessage',
            },
            formTitle: {
              default: 'c-electronic-instruction-request.formTitle',
              sagemax: 'c-sagemax-electronic-instruction-request.formTitle',
            },
            downloadTitle: {
              default: 'c-electronic-instruction-request.downloadTitle',
              sagemax: 'c-sagemax-electronic-instruction-request.downloadTitle',
            },
            downloadDisclaimer: {
              default: 'c-electronic-instruction-request.downloadDisclaimer',
              sagemax: 'c-sagemax-electronic-instruction-request.downloadDisclaimer',
            },
            downloadButton: {
              default: 'c-electronic-instruction-request.downloadButton',
              sagemax: 'c-sagemax-electronic-instruction-request.downloadButton',
            },
            inputLabelGender: {
              default: 'c-electronic-instruction-request.inputLabelGender',
              sagemax: 'c-sagemax-electronic-instruction-request.inputLabelGender',
            },
            inputLabelTitle: {
              default: 'c-electronic-instruction-request.inputLabelTitle',
              sagemax: 'c-sagemax-electronic-instruction-request.inputLabelTitle',
            },
            inputLabelFirstName: {
              default: 'c-electronic-instruction-request.inputLabelFirstName',
              sagemax: 'c-sagemax-electronic-instruction-request.inputLabelFirstName',
            },
            inputLabelLastName: {
              default: 'c-electronic-instruction-request.inputLabelLastName',
              sagemax: 'c-sagemax-electronic-instruction-request.inputLabelLastName',
            },
            inputLabelCompanyName: {
              default: 'c-electronic-instruction-request.inputLabelCompanyName',
              sagemax: 'c-sagemax-electronic-instruction-request.inputLabelCompanyName',
            },
            inputLabelAddress: {
              default: 'c-electronic-instruction-request.inputLabelAddress',
              sagemax: 'c-sagemax-electronic-instruction-request.inputLabelAddress',
            },
            inputLabelZipCode: {
              default: 'c-electronic-instruction-request.inputLabelZipCode',
              sagemax: 'c-sagemax-electronic-instruction-request.inputLabelZipCode',
            },
            inputLabelCity: {
              default: 'c-electronic-instruction-request.inputLabelCity',
              sagemax: 'c-sagemax-electronic-instruction-request.inputLabelCity',
            },
            inputLabelCountry: {
              default: 'c-electronic-instruction-request.inputLabelCountry',
              sagemax: 'c-sagemax-electronic-instruction-request.inputLabelCountry',
            },
            inputLabelEmail: {
              default: 'c-electronic-instruction-request.inputLabelEmail',
              sagemax: 'c-sagemax-electronic-instruction-request.inputLabelEmail',
            },
            inputLabelPhone: {
              default: 'c-electronic-instruction-request.inputLabelPhone',
              sagemax: 'c-sagemax-electronic-instruction-request.inputLabelPhone',
            },
            inputLabelComment: {
              default: 'c-electronic-instruction-request.inputLabelComment',
              sagemax: 'c-sagemax-electronic-instruction-request.inputLabelComment',
            },
            inputPlaceholderGender: {
              default: '',
              sagemax: 'c-sagemax-electronic-instruction-request.inputPlaceholderGender',
            },
            inputPlaceholderTitle: {
              default: '',
              sagemax: 'c-sagemax-electronic-instruction-request.inputPlaceholderTitle',
            },
            inputPlaceholderFirstName: {
              default: '',
              sagemax: 'c-sagemax-electronic-instruction-request.inputPlaceholderFirstName',
            },
            inputPlaceholderLastName: {
              default: '',
              sagemax: 'c-sagemax-electronic-instruction-request.inputPlaceholderLastName',
            },
            inputPlaceholderCompanyName: {
              default: '',
              sagemax: 'c-sagemax-electronic-instruction-request.inputPlaceholderCompanyName',
            },
            inputPlaceholderAddress: {
              default: '',
              sagemax: 'c-sagemax-electronic-instruction-request.inputPlaceholderAddress',
            },
            inputPlaceholderZipCode: {
              default: '',
              sagemax: 'c-sagemax-electronic-instruction-request.inputPlaceholderZipCode',
            },
            inputPlaceholderCity: {
              default: '',
              sagemax: 'c-sagemax-electronic-instruction-request.inputPlaceholderCity',
            },
            inputPlaceholderCountry: {
              default: '',
              sagemax: 'c-sagemax-electronic-instruction-request.inputPlaceholderCountry',
            },
            inputPlaceholderEmail: {
              default: '',
              sagemax: 'c-sagemax-electronic-instruction-request.inputPlaceholderEmail',
            },
            inputPlaceholderPhone: {
              default: '',
              sagemax: 'c-sagemax-electronic-instruction-request.inputPlaceholderPhone',
            },
            inputPlaceholderComment: {
              default: '',
              sagemax: 'c-sagemax-electronic-instruction-request.inputPlaceholderComment',
            },
          },
        },
      };
    },

    /**
     * Vuelidate configuration.
     */
    validations: {
      formData: {
        gender: {
          required,
        },
        firstName: {
          required,
        },
        lastName: {
          required,
        },
        companyName: {
          required,
        },
        address: {
          required,
        },
        zipCode: {
          required,
        },
        city: {
          required,
        },
        country: {
          required,
        },
        email: {
          required,
          email,
        },
        acceptGeneralTermsOfUse: {
          checked: (value) => !!value,
        },
      },
    },

    computed: {
      /**
       * Handles the open state of the modal.
       */
      isModalOpen: {
        get() {
          return !!this.item;
        },
        set(open) {
          if (!open) {
            this.eifuStore.requestElectronicInstructionItem = null;
          }
        },
      },

      /**
       * Returns a definition of BEM modifiers.
       *
       * @returns {Object}
       */
      formModifiers() {
        const { theme } = this;

        return {
          theme,
        };
      },
    },
    // watch: {},

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    mounted() {
      window.addEventListener('resizeend', this.updatePropertyOverflowState);

      setTimeout(this.updatePropertyOverflowState); // Immediate execution causes sometimes calculation errors.
    },
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    beforeUnmount() {
      window.removeEventListener('resizeend', this.updatePropertyOverflowState);
    },
    // unmounted() {},

    methods: {
      /**
       * Helper function.
       */
      getElectronicInstructionBrandNames,

      /**
       * Updates the reference, if limited height properties have currently an overflow.
       */
      updatePropertyOverflowState() {
        this.overflowingProperties = Object.values(this.PROPERTY_REF_MAP).filter((ref) => {
          const element = this.$refs[ref];

          // NOTE: due to slight differences in fontsize/lineheight depending on the theme, we allow for a small tolerance
          return !!(element && element.scrollHeight - element.clientHeight > 3);
        });
      },

      /**
       * Handles the click event of an expand/collapse toggle button for a limited height property.
       *
       * @param {String} refIdentifier - The ref identifier for the property.
       */
      onPropertyCollapseToggle(refIdentifier) {
        this.expandedProperties[refIdentifier] = !this.expandedProperties[refIdentifier];
      },

      /**
       * Calculates BEM modifiers for properties with limited height.
       *
       * @param {String} refIdentifier - The ref name by which the property can be identified.
       *
       * @returns {Object}
       */
      getModifiersForLimitedProperty(refIdentifier) {
        return {
          limit: !this.expandedProperties[refIdentifier],
          hasOverflow: this.overflowingProperties.includes(refIdentifier),
        };
      },

      /**
       * Handles the click event on the download button.
       *
       * @param {Event} event - The click event instance.
       */
      onClickDownload(event) {
        const { item } = this;

        event.preventDefault();
        downloadFileByUrl(item.fileName, item.fileUrl);
      },

      /**
       * Form submit handler.
       */
      onSubmit() {
        this.v$.$touch();

        if (this.v$.$pending || this.v$.$error) {
          return;
        }

        const { item, formData } = this;

        this.eifuStore
          .apiRequestHardCopy({
            fileId: item.fileId,
            validAsOf: item.validAsOf ?? null,
            brandNames: item.brandNames ?? [],
            referenceNumbers: item.referenceNumbers ?? [],
            udiDiNumbers: item.udiDiNumbers ?? [],
            formData,
          })
          .then(() => {
            this.showConfirmation = true;
            this.v$.$reset();
          });
      },
    },
    // render() {},
  };
</script>

<style lang="scss">
  @use 'sass:math';
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/extends' as *;
  @use '@/setup/scss/functions';

  .c-electronic-instruction-request {
    $this: &;

    &--theme-sagemax {
      #{$this}__title {
        margin-bottom: functions.calc-rem(variables.$spacing--40);
      }

      #{$this}__meta {
        margin-bottom: functions.rem(variables.$spacing--25);

        @include mixins.media(md) {
          margin-bottom: functions.rem(variables.$spacing--40);
        }
      }

      #{$this}__footer {
        justify-content: flex-end;
        margin-top: 0;
      }
    }

    &__title {
      @extend %hyphens;

      margin-bottom: functions.rem(variables.$spacing--25);

      @include mixins.media(md) {
        margin-bottom: functions.rem(variables.$spacing--30);
      }
    }

    &__download {
      margin-bottom: functions.rem(variables.$spacing--60);
    }

    &__download-title {
      margin-bottom: 0;
    }

    &__download-disclaimer {
      color: functions.get-theme-var('color-primary-1');
      font-weight: variables.$font-weight--semi-bold;
    }

    &__meta {
      margin-bottom: functions.rem(variables.$spacing--30);

      @include mixins.media(md) {
        margin-bottom: functions.rem(variables.$spacing--40);
      }
    }

    &__brand {
      @include mixins.font(variables.$font-size--14, variables.$line-height--22, variables.$font-weight--semi-bold);

      margin-bottom: functions.rem(variables.$spacing--10);

      @include mixins.media(md) {
        @include mixins.font(variables.$font-size--16);
      }
    }

    &__properties {
      @include mixins.font(variables.$font-size--14, variables.$line-height--22);

      margin-bottom: 0;
    }

    &__property {
      &--limit {
        @include mixins.line-clamp(
          3,
          functions.strip-unit(math.div(variables.$line-height--22, variables.$font-size--14))
        );
      }
    }

    &__property-name {
      float: left;
      font-weight: variables.$font-weight--regular;
    }

    &__property-value {
      margin-bottom: 0;
      font-weight: variables.$font-weight--semi-bold;

      &::before {
        content: '\00a0';
      }
    }

    &__property-toggle-wrapper {
      margin-bottom: functions.rem(variables.$spacing--10);

      #{$this}__property:not(#{$this}__property--has-overflow) + & {
        display: none;
      }
    }

    &__property-toggle {
      @extend %button-reset;

      color: functions.get-theme-var('color-primary-1');
      font-weight: variables.$font-weight--semi-bold;
      text-align: left;
      text-decoration: underline;
    }

    &__form {
      &--theme-sagemax {
        #{$this}__terms-link {
          font-weight: variables.$font-weight--semi-bold;
          text-decoration: none;
        }
      }
    }

    &__form-row {
      margin-bottom: functions.rem(variables.$spacing--20);

      @include mixins.media(md) {
        width: 50%;
      }
    }

    &__footer {
      display: flex;
      flex-wrap: wrap;
      margin-top: functions.rem(variables.$spacing--40);

      @include mixins.media(sm) {
        flex-wrap: nowrap;
        justify-content: space-between;
      }

      @include mixins.media(md) {
        margin-top: functions.rem(variables.$spacing--60);
      }
    }

    &__button-submit {
      margin-bottom: functions.rem(variables.$spacing--30);

      @include mixins.media(sm) {
        order: 2;
        margin-bottom: 0;
      }
    }

    &__button-back {
      @extend %button-reset;
      @extend %link--primary-2;

      display: flex;
      align-items: center;

      @include mixins.media(sm) {
        order: 1;
      }

      .e-icon-sprite {
        width: functions.rem(22px);
        height: functions.rem(22px);
        margin-right: functions.rem(variables.$spacing--10);
      }
    }

    &__progress {
      display: flex;
      justify-content: center;

      .e-progress {
        width: functions.rem(30px);
        height: functions.rem(30px);
      }
    }

    &__close-button-wrapper {
      display: flex;
      justify-content: flex-end;
    }

    &__close-button {
      margin-top: functions.rem(variables.$spacing--40);
    }
  }
</style>
